import { createFileRoute } from "@tanstack/react-router";
import {
  IndustryCityMarketingPage,
  buildIndustryCityMarketingHead,
  type IndustryCityMarketingConfig,
} from "@/components/IndustryCityMarketingPage";
import { CASTLE_ROCK_PROFILE } from "@/lib/cityProfiles/castle-rock";

const CONFIG: IndustryCityMarketingConfig = {
  slug: "roofing-marketing-castle-rock",
  industry: "Roofing",
  industryLower: "roofing",
  ...CASTLE_ROCK_PROFILE,
  metaTitle: "Roofing Marketing in Castle Rock, CO | SEO, Maps, Ads & Web",
  metaDescription: "Owner-led roofing marketing for Castle Rock roofers \u2014 SEO, Google Maps, web design, and ads across The Meadows and Crystal Valley. Flat $1,650/mo, no contracts.",
  h1: { lead: "Roofing marketing in", gold: "Castle Rock", trail: "\u2014 from The Meadows to Founders Village" },
  eyebrow: "Castle Rock Roofing Marketing",
  heroDescription: "We help Castle Rock roofers own the Map Pack across 80104, 80108, and 80109, win 'roof replacement' and 'hail damage roof repair' searches, and keep crews booked year-round.",
  intro: "Castle Rock sits squarely in Colorado's hail belt, which means every major storm puts thousands of Castle Rock roofs \u2014 from The Meadows to Founders Village \u2014 into insurance-claim territory overnight. The roofers who win aren't the ones with the most door-knockers; they're the ones homeowners find first when they Google 'hail damage roof repair Castle Rock' the morning after. Storm-chasing out-of-state crews flood the Map Pack within days, so local roofers need a durable SEO and Google Maps footprint built before the storm, plus ads ready to switch on the hour it hits. That's what we build \u2014 owner-led, flat $1,650/month, no contracts.",
  painPoints: [
  "Out-of-state storm chasers flooding Castle Rock search results and the Map Pack within days of every hailstorm.",
  "Feast-or-famine lead flow \u2014 slammed after storms, dead in the long dry stretches between them.",
  "High-ticket full roof replacements and insurance jobs in Crystal Valley and Castlewood Ranch going to whoever shows up first on a homeowner's phone."
],
  services: {
  "seo": "Rank for 'roofing company Castle Rock,' 'hail damage roof repair,' and 'roof replacement 80104' across The Meadows, Crystal Valley, Founders Village.",
  "googleMaps": "Top-3 Map Pack for Castle Rock roofers \u2014 before/after storm photos, insurance-claim posts, and a review engine that out-paces storm chasers.",
  "webDesign": "Hand-coded roofing sites with storm-response banners, financing and insurance-claim pages, and sticky tap-to-call for homeowners on the ladder.",
  "ppc": "Storm-triggered Google Ads and LSA \u2014 we ramp bids the hour hail hits Castle Rock and pull back when the phones are full.",
  "geoAi": "Get cited when Castle Rock homeowners ask ChatGPT or Gemini 'who's the best roofer near me after hail?'",
  "socialMedia": "Job-site proof content \u2014 tear-offs, drone shots, and finished roofs \u2014 that builds trust before the next storm."
},
  trustPoints: [
  "Owner-led from our Colorado Springs HQ \u2014 we know Castle Rock roofers' market firsthand.",
  "300+ Colorado clients served since 2006 across roofing, plumbing, HVAC, and electrical.",
  "Flat $1,650/month marketing, websites from $1,850 \u2014 published publicly.",
  "Talk to Chris directly. No account managers or offshore handoffs."
],
  testimonial: { quote: "Before we started, every storm meant watching out-of-state crews take our neighborhoods. Now we're in the top three on Maps when hail hits, and the phones light up for us first.", role: "Roofing company owner", location: "Castle Rock, CO" },
  faqs: [
  {
    "q": "How do I get more roofing leads in Castle Rock?",
    "a": "Build a Map Pack and SEO footprint before storm season, then layer storm-triggered Google Ads and LSA on top. When hail hits Castle Rock, you want to already rank for 'hail damage roof repair' and 'roof replacement' \u2014 not be scrambling to get found."
  },
  {
    "q": "How much does roofing marketing cost in Castle Rock?",
    "a": "Our Growth Marketing package \u2014 SEO, Google Maps, GEO AI, and social \u2014 is $1,650/month flat. A new roofing website starts at $1,850 one-time, owned outright, with in-house financing available. Prices are public because Castle Rock roofers deserve a straight answer."
  },
  {
    "q": "How long until I rank in the Castle Rock Map Pack?",
    "a": "Most Castle Rock roofing clients see Map Pack movement within 60\u2013120 days, and citywide top-3 across The Meadows, Crystal Valley, Founders Village usually takes 4\u20136 months of consistent work. It compounds month over month."
  },
  {
    "q": "Do you cover Castle Pines, Larkspur, Sedalia, and Parker too?",
    "a": "Yes. We build service-area pages and Map Pack signals for every area your trucks reach, including Castle Pines, Larkspur, Sedalia, and Parker."
  },
  {
    "q": "When should I ramp up roofing ads in Castle Rock?",
    "a": "Ahead of hail season (May\u2013August). We build the campaigns in advance so they can go live the moment demand spikes, and scale them back when your schedule is full."
  },
  {
    "q": "Are you actually local to Castle Rock?",
    "a": "We're headquartered in Colorado Springs (3210 E Woodmen Rd) and work Castle Rock weekly. Verified Google Business Profile, 4.9\u2605 average, Colorado-owned since 2006."
  },
  {
    "q": "Do you lock me into a contract?",
    "a": "No. Every engagement is month-to-month \u2014 no early-termination fees. We earn the renewal every month."
  }
],
  related: [
  {
    "to": "/roofing-marketing-colorado",
    "label": "Roofing marketing across Colorado",
    "description": "Statewide hub for Colorado roofers."
  },
  {
    "to": "/castle-rock-digital-marketing",
    "label": "Castle Rock digital marketing hub",
    "description": "Every service we offer for Castle Rock businesses."
  },
  {
    "to": "/roofing-marketing-colorado-springs",
    "label": "Roofing marketing in Colorado Springs",
    "description": "Same playbook tuned for Colorado Springs roofers."
  },
  {
    "to": "/roofing-marketing-parker",
    "label": "Roofing marketing in Parker",
    "description": "Same playbook tuned for Parker roofers."
  },
  {
    "to": "/hvac-marketing-castle-rock",
    "label": "HVAC marketing in Castle Rock",
    "description": "Our HVAC playbook for Castle Rock."
  },
  {
    "to": "/pricing",
    "label": "Flat, public pricing",
    "description": "$1,650/mo marketing, websites from $1,850."
  }
],
  showPricing: true,
};

export const Route = createFileRoute("/roofing-marketing-castle-rock")({
  head: () => buildIndustryCityMarketingHead(CONFIG),
  component: () => <IndustryCityMarketingPage config={CONFIG} />,
});
